import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { InsertDriveFileOutlined as InsertDriveFileOutlinedIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import AddReportModal from './AddReportModal';
import { useAuditorVisitDetailContext } from '../contexts/AuditorVisitDetailContext';
import { SqoFile } from '@/common';
import ReportFilesModal from './ReportsFilesModal';
import { useFormPageStore } from '../stores/UseReportesStore';
import { Report } from '../types';

const Reports = () => {
  const { t } = useTranslation();
  const [isAddReportModalOpen, setIsAddReportModalOpen] = useState(false);
  const { auditorVisit } = useAuditorVisitDetailContext();
  const reportPageStore = useFormPageStore();
  const [reportFilesModal, setReportFilesModal] = useState<{
    isOpen: boolean;
    reportId: string;
    files: SqoFile[];
  }>({
    isOpen: false,
    reportId: '',
    files: [],
  });

  useEffect(() => {
    return () => {
      reportPageStore.reset();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!auditorVisit.reports) {
      return;
    }
    reportPageStore.setReports(auditorVisit.reports);
    reportPageStore.setIsLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditorVisit]);

  return (
    <Box sx={{ pt: '2rem' }}>
      <AddReportModal isOpen={isAddReportModalOpen} setIsOpen={setIsAddReportModalOpen} />
      <ReportFilesModal
        reportId={reportFilesModal.reportId}
        reports={reportPageStore.reports}
        show={reportFilesModal.isOpen}
        setShow={(show: boolean) =>
          setReportFilesModal({
            isOpen: show,
            files: reportFilesModal.files,
            reportId: reportFilesModal.reportId,
          })
        }
        disabled={
          (auditorVisit.status ? Number(auditorVisit.status.slice(0, 2)) > 34 : false) ||
          !auditorVisit.isCurrentAuditorTeamLeader
        }
        setReports={(reports: Report[]) => reportPageStore.setReports(reports)}
      />
      {!!auditorVisit.isCurrentAuditorTeamLeader && (
        <Box>
          <Button
            variant="contained"
            onClick={() => setIsAddReportModalOpen(true)}
            fullWidth
            disabled={auditorVisit.status ? Number(auditorVisit.status.slice(0, 2)) > 34 : false}
          >
            {t('Add new report', { defaultValue: 'Add new report' })}
          </Button>
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <List>
          {!!auditorVisit.reports &&
            auditorVisit.reports.map((report) => (
              <ListItem key={report.id} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.25rem' }}
                  secondaryTypographyProps={{ fontSize: '1rem' }}
                  primary={report.name}
                  secondary={
                    <Box>
                      <Typography>
                        {t('Note', { defaultValue: 'Note' })}: {report.note}
                      </Typography>
                      <Typography>
                        {t('Status', { defaultValue: 'Status' })}: {report.status}
                      </Typography>
                    </Box>
                  }
                />
                <Tooltip title={t('Files', { defaultValue: 'Files' })} placement="top">
                  <IconButton
                    onClick={() =>
                      setReportFilesModal({
                        isOpen: true,
                        reportId: report.id,
                        files: report.files,
                      })
                    }
                  >
                    <InsertDriveFileOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </ListItem>
            ))}
        </List>
      </Box>
    </Box>
  );
};

export default Reports;
